/**
 * CR120: Page Styling Types
 * 
 * Types for background configuration, page takeovers, and seasonal theming.
 */

// ================================================================
// BACKGROUND CONFIGURATION
// ================================================================

export type BackgroundType = "solid" | "gradient" | "image" | "takeover";

export interface BackgroundGradient {
  color1: string; // HSL string
  color2: string; // HSL string
  angle: number;  // degrees
}

export interface BackgroundImageConfig {
  url: string | null;
  opacity: number;      // 0-100
  blur: number;         // 0-20 px
  overlayColor: string; // HSL string
  overlayOpacity: number; // 0-100
}

export interface BackgroundConfig {
  type: BackgroundType;
  solidColor?: string;
  gradient?: BackgroundGradient;
  image?: BackgroundImageConfig;
  takeoverThemeId?: string;
}

// ================================================================
// PAGE TAKEOVER CONFIGURATION
// ================================================================

export interface TakeoverConfig {
  themeId: string | null;
  applyToHero: boolean;
  applyToBackground: boolean;
  applyToCardsAccent: boolean;
  applyToFilters: boolean;
}

// ================================================================
// SEASONAL / EVENT PREVIEW
// ================================================================

export type SeasonalPreviewId = 
  | "default"
  | "spring"
  | "summer" 
  | "fall"
  | "winter"
  | "black_friday"
  | "valentines_day"
  | "canada_day"
  | "singles_day";

export interface SeasonalTheme {
  id: SeasonalPreviewId;
  label: string;
  description: string;
  colors?: {
    primary?: string;
    accent?: string;
    background?: string;
  };
}

// Mocked seasonal themes (will be replaced by Theme Scheduler integration)
export const MOCK_SEASONAL_THEMES: SeasonalTheme[] = [
  { id: "default", label: "Default", description: "Standard appearance" },
  { id: "spring", label: "Spring", description: "Fresh greens and pastels", colors: { primary: "142 70% 45%", accent: "325 80% 60%" } },
  { id: "summer", label: "Summer", description: "Warm sunshine tones", colors: { primary: "38 90% 50%", accent: "200 80% 55%" } },
  { id: "fall", label: "Fall", description: "Warm autumn colors", colors: { primary: "25 85% 45%", accent: "45 90% 50%" } },
  { id: "winter", label: "Winter", description: "Cool blues and silvers", colors: { primary: "210 60% 50%", accent: "200 70% 70%" } },
  { id: "black_friday", label: "Black Friday", description: "Bold black and gold", colors: { primary: "0 0% 10%", accent: "45 100% 50%" } },
  { id: "valentines_day", label: "Valentine's Day", description: "Romantic reds and pinks", colors: { primary: "350 80% 55%", accent: "330 85% 65%" } },
  { id: "canada_day", label: "Canada Day", description: "Red and white celebration", colors: { primary: "0 85% 50%", accent: "0 0% 100%" } },
  { id: "singles_day", label: "Singles Day 11.11", description: "Vibrant celebration", colors: { primary: "280 70% 50%", accent: "45 90% 55%" } },
];

// Mocked takeover themes (will be replaced by Theme Labs integration)
export interface MockTakeoverTheme {
  id: string;
  name: string;
  description: string;
}

export const MOCK_TAKEOVER_THEMES: MockTakeoverTheme[] = [
  { id: "none", name: "None", description: "No takeover theme" },
  { id: "black_friday_2024", name: "Black Friday 2024", description: "Bold dark theme with gold accents" },
  { id: "holiday_winter", name: "Holiday Winter", description: "Festive winter wonderland theme" },
  { id: "spring_refresh", name: "Spring Refresh", description: "Fresh pastel spring theme" },
];

// ================================================================
// PREVIEW DOCK MODES
// ================================================================

export type PreviewDockMode = "right" | "bottom";

// ================================================================
// DEFAULTS
// ================================================================

export const DEFAULT_BACKGROUND_CONFIG: BackgroundConfig = {
  type: "solid",
  solidColor: "0 0% 98%", // Light gray
};

export const DEFAULT_TAKEOVER_CONFIG: TakeoverConfig = {
  themeId: null,
  applyToHero: false,
  applyToBackground: false,
  applyToCardsAccent: false,
  applyToFilters: false,
};
